// TODO: next PR for GEN-2388 will add properly components based on helix design system with the refactored code.
import { useCallback, useEffect, useMemo, useRef } from 'react'
import { useIntl } from 'react-intl'
import { Navigate, useLocation, useNavigate } from 'react-router-dom'
import { featureFlags } from '../config/featureFlags'
import {
  CONSENT_IMAGE,
  ConsentFlowCard,
  ConsentLanguageErrorPanel,
  ContextLoadingScreen,
  ErrorPanel,
  LoadingPanel,
  WorkflowProgress,
} from '../features/consent/consentOptions/ConsentFlowLayout'
import { ConsentIntroScreen, ConsentSignScreen } from '../features/consent/consentOptions/PrimaryConsentStep'
import { SecondaryChoiceScreen, SecondaryIntroScreen } from '../features/consent/consentOptions/SecondaryConsentStep'
import { getPrimaryCheckboxText, normalizePath } from '../features/consent/consentOptions/consentOptionsUtils'
import { usePrimaryConsent } from '../features/consent/consentOptions/usePrimaryConsent'
import { useSecondaryConsent } from '../features/consent/consentOptions/useSecondaryConsent'
import { secondaryAnswerSubjects, subjectLegend, subjectRows } from '../features/consent/consentSubjects'
import { useConsentConfig } from '../features/consent/hooks/useConsentConfig'
import { useConsentContext } from '../features/consent/hooks/useConsentContext'
import { useConsentLanguage } from '../features/consent/hooks/useConsentLanguage'
import { useSecondaryConsentConfig } from '../features/consent/hooks/useSecondaryConsentConfig'
import { acceptsHealthRecordsAccess, HEALTH_RECORDS_ACCESS_ITEM_ID } from '../features/hrc/healthRecordsConsent'
import { useProfileName } from '../hooks/useProfileName'
import { useStageTracking } from '../hooks/useStageTracking'
import { track } from '../utils/analytics'
import { isPractitionerMediatedConsentFlow, resolveUserConsentFlow } from '../utils/consentFlowHelper'
import { withSupportMessage } from '../utils/errorMessage'

const ConsentOptions = () => {
  const intl = useIntl()
  const location = useLocation()
  const navigate = useNavigate()
  const profileName = useProfileName()
  const path = normalizePath(location.pathname)
  const consentContext = useConsentContext()
  const {
    language: consentLanguage,
    loading: consentLanguageLoading,
    error: consentLanguageError,
  } = useConsentLanguage()
  const secondaryConsentSource = useSecondaryConsentConfig()
  const consentConfig = useConsentConfig(
    secondaryConsentSource.payload,
    secondaryConsentSource.loading,
    secondaryConsentSource.error
  )
  const secondaryConsentConfigId = consentConfig.consentConfig?.configId ?? null
  const secondaryConsentConfigVersionId = consentConfig.consentConfig?.configVersionId ?? null
  const secondaryConsentConfig = useMemo(() => {
    if (!secondaryConsentConfigId || !secondaryConsentConfigVersionId) return null
    return {
      configId: secondaryConsentConfigId,
      configVersionId: secondaryConsentConfigVersionId,
    }
  }, [secondaryConsentConfigId, secondaryConsentConfigVersionId])
  const prevPathnameRef = useRef(location.pathname)
  const primaryFlowCompletedRef = useRef(false)
  const markPrimaryFlowCompleted = useCallback(() => {
    primaryFlowCompletedRef.current = true
  }, [])
  const primaryConsent = usePrimaryConsent(
    consentContext.context,
    consentConfig.flowStartedAt,
    markPrimaryFlowCompleted,
    consentLanguage,
    consentContext.refresh
  )
  const secondaryConsent = useSecondaryConsent(
    secondaryConsentConfig,
    intl.locale,
    consentContext.context?.clientConfigVersion
  )

  useStageTracking(path)

  useEffect(() => {
    primaryFlowCompletedRef.current = primaryConsent.isFlowCompleted
  }, [primaryConsent.isFlowCompleted])

  useEffect(() => {
    if (consentLanguageError === 'consent_not_applicable') {
      primaryFlowCompletedRef.current = true
    }
  }, [consentLanguageError])

  useEffect(() => {
    return () => {
      if (!primaryFlowCompletedRef.current) {
        track('consent.drop_off', {
          stage: normalizePath(prevPathnameRef.current),
          reason: 'spa_navigation',
        })
      }
    }
  }, [])

  useEffect(() => {
    const onPopState = () => {
      track('consent.back_navigation', {
        from: normalizePath(location.pathname),
        to: normalizePath(prevPathnameRef.current),
      })
      prevPathnameRef.current = location.pathname
    }
    window.addEventListener('popstate', onPopState)
    return () => window.removeEventListener('popstate', onPopState)
  }, [location.pathname])

  // A reload or a direct link past the first card has no in-memory draft. Start over
  // so completion cannot write those cards as unset. Continue keeps this component mounted.
  const openedPastFirstCard = useRef(
    path.startsWith('/consents/secondary/') && Number(path.split('/').filter(Boolean)[2]) >= 3
  )
  useEffect(() => {
    if (!openedPastFirstCard.current) return
    openedPastFirstCard.current = false
    navigate('/consents/secondary/2', { replace: true })
  }, [navigate])

  const selectedSecondaryPage = path.startsWith('/consents/secondary/')
    ? Number(path.split('/').filter(Boolean)[2])
    : undefined
  const selectedSecondaryIndex =
    selectedSecondaryPage !== undefined && Number.isInteger(selectedSecondaryPage) && selectedSecondaryPage >= 2
      ? selectedSecondaryPage - 2
      : undefined
  const selectedSecondaryItem =
    selectedSecondaryIndex !== undefined && Number.isInteger(selectedSecondaryIndex)
      ? consentConfig.secondaryItems[selectedSecondaryIndex]
      : undefined
  const secondarySubjects = useMemo(
    () => secondaryAnswerSubjects(consentContext.context, consentContext.dualSecondary),
    [consentContext.context, consentContext.dualSecondary]
  )

  if (consentContext.contextLoading) {
    return <ContextLoadingScreen />
  }

  if (consentContext.contextError || consentContext.contextMissing || !consentContext.hasContext) {
    return (
      <ConsentFlowCard
        imageSrc={CONSENT_IMAGE}
        layout="vertical"
        progress={
          <WorkflowProgress
            current={[consentContext.workflowSteps.consent]}
            next={[consentContext.workflowSteps.secondary, consentContext.workflowSteps.profile]}
          />
        }
      >
        <ErrorPanel
          message={
            consentContext.contextError
              ? withSupportMessage(
                  intl,
                  intl.formatMessage({
                    id: 'consentOptions.error.couldNotLoadTestPatient',
                    defaultMessage: "We couldn't load the test and patient details.",
                  })
                )
              : intl.formatMessage({
                  id: 'consentOptions.error.couldNotLoadContext',
                  defaultMessage: "We couldn't load the test and patient details required to provide consent.",
                })
          }
        />
      </ConsentFlowCard>
    )
  }

  const practitionerMediated = isPractitionerMediatedConsentFlow(resolveUserConsentFlow(consentContext.context))
  if (practitionerMediated && (path === '/consents' || path === '/consents/sign')) {
    return <Navigate replace to="/consents/secondary" />
  }

  const primaryStepProgress = (
    <WorkflowProgress
      current={[consentContext.workflowSteps.consent]}
      next={[consentContext.workflowSteps.secondary, consentContext.workflowSteps.profile]}
    />
  )

  if (path === '/consents' || path === '/consents/sign') {
    if (consentLanguageLoading) {
      return (
        <ConsentFlowCard imageSrc={CONSENT_IMAGE} progress={primaryStepProgress}>
          <LoadingPanel
            message={intl.formatMessage({
              id: 'consentOptions.loadingPanel.message',
              defaultMessage: 'Loading consent details...',
            })}
          />
        </ConsentFlowCard>
      )
    }
    if (consentLanguageError === 'consent_not_applicable') {
      return (
        <ConsentIntroScreen
          alreadySigned={primaryConsent.confirmed}
          noPendingPrimaryOrders
          workflowSteps={consentContext.workflowSteps}
        />
      )
    }
    if (!consentLanguage) {
      // Also guards documentContent completeness (FAM-108 / G3): usePrimaryConsent.confirm() only
      // sends documentContent when consentLanguage is truthy, and it's passed the same
      // consentLanguage value used here. Rendering ConsentSignScreen (and its confirm handler)
      // before this check would let a user submit consent with no documentContent recorded.
      return (
        <ConsentFlowCard imageSrc={CONSENT_IMAGE} progress={primaryStepProgress}>
          <ConsentLanguageErrorPanel error={consentLanguageError} />
        </ConsentFlowCard>
      )
    }
    if (path === '/consents/sign') {
      const checkboxText = getPrimaryCheckboxText(consentContext.context, consentLanguage, intl.locale)
      if (checkboxText === null) {
        return (
          <ConsentFlowCard imageSrc={CONSENT_IMAGE} progress={primaryStepProgress}>
            <ConsentLanguageErrorPanel error="missing_content" />
          </ConsentFlowCard>
        )
      }
      return (
        <ConsentSignScreen
          checkboxText={checkboxText}
          agreed={primaryConsent.agreed}
          onAgreedChange={primaryConsent.setAgreed}
          confirmed={primaryConsent.confirmed}
          signedAt={primaryConsent.signedAt}
          saving={primaryConsent.saving}
          refreshing={primaryConsent.refreshing}
          errorMessage={primaryConsent.error}
          onConfirm={primaryConsent.confirm}
          primaryFlowNotice={primaryConsent.showPrimaryFlowNotice}
          workflowSteps={consentContext.workflowSteps}
          language={consentLanguage}
        />
      )
    }
    return <ConsentIntroScreen alreadySigned={primaryConsent.confirmed} workflowSteps={consentContext.workflowSteps} />
  }

  // Secondary consent texts load independently of primary, so gate only the secondary steps on them.
  if (path.startsWith('/consents/secondary')) {
    if (consentConfig.consentConfigLoading) {
      return (
        <ConsentFlowCard
          imageSrc={CONSENT_IMAGE}
          layout="vertical"
          progress={
            <WorkflowProgress
              current={[consentContext.workflowSteps.consent]}
              next={[consentContext.workflowSteps.secondary, consentContext.workflowSteps.profile]}
            />
          }
        >
          <LoadingPanel
            message={intl.formatMessage({
              id: 'consentOptions.loadingPanel.message',
              defaultMessage: 'Loading consent details...',
            })}
          />
        </ConsentFlowCard>
      )
    }
    if (consentConfig.consentConfigError) {
      return (
        <ConsentFlowCard
          imageSrc={CONSENT_IMAGE}
          layout="vertical"
          progress={
            <WorkflowProgress
              current={[consentContext.workflowSteps.consent]}
              next={[consentContext.workflowSteps.secondary, consentContext.workflowSteps.profile]}
            />
          }
        >
          <ErrorPanel message={consentConfig.consentConfigError} />
        </ConsentFlowCard>
      )
    }
  }

  if (path === '/consents/secondary') {
    return (
      <SecondaryIntroScreen
        hasItems={consentConfig.secondaryItems.length > 0}
        workflowSteps={consentContext.workflowSteps}
      />
    )
  }

  if (path === '/consents/secondary/1') {
    return <Navigate replace to={consentConfig.secondaryItems.length > 0 ? '/consents/secondary/2' : '/home'} />
  }

  if (path.startsWith('/consents/secondary/') && selectedSecondaryItem) {
    const item = selectedSecondaryItem.item
    const keySelf = `${item.id}:SELF`
    const keyDep = `${item.id}:DEPENDENT`
    const valueSingle = secondaryConsent.toggles[item.id]
    const valueSelf = secondaryConsent.toggles[keySelf]
    const valueDependent = secondaryConsent.toggles[keyDep]
    const rows = secondarySubjects ? subjectRows(item.id, secondarySubjects, consentContext.dualSecondary) : null
    const healthRecords = item.id === HEALTH_RECORDS_ACCESS_ITEM_ID
    const selfName = [profileName.firstName, profileName.lastName]
      .map((part) => part?.trim())
      .filter(Boolean)
      .join(' ')
    const selfSubtitle =
      healthRecords && selfName
        ? intl.formatMessage({ id: 'healthRecords.consent.forYouNamed' }, { name: selfName })
        : undefined
    const dependentName = healthRecords
      ? consentContext.context?.patientName?.trim() || consentContext.context?.patientFirstName?.trim()
      : consentContext.context?.patientFirstName?.trim() || consentContext.context?.patientName?.trim()
    const dependentSubtitle = rows
      ? undefined
      : dependentName
        ? intl.formatMessage(
            { id: 'consentOptions.secondaryChoice.forPatient', defaultMessage: 'For {name}' },
            { name: dependentName }
          )
        : intl.formatMessage({
            id: 'consentOptions.secondaryChoice.forYourChild',
            defaultMessage: 'For your child',
          })

    return (
      <SecondaryChoiceScreen
        item={item}
        dualSecondary={consentContext.dualSecondary}
        dependentSubtitle={dependentSubtitle}
        selfSubtitle={selfSubtitle}
        hasStandingSelfSelection={secondaryConsent.standingPersonSelections.has(keySelf)}
        subjectRows={rows?.map(({ subject, key }) => ({
          key,
          legend:
            subject.kind === 'self' && selfSubtitle
              ? selfSubtitle
              : subjectLegend(subject, intl, consentContext.context),
          value: secondaryConsent.toggles[key],
          existingSelection: subject.kind === 'self' && secondaryConsent.standingPersonSelections.has(key),
          onSelect: (nextValue: boolean) => secondaryConsent.setToggles((prev) => ({ ...prev, [key]: nextValue })),
        }))}
        workflowSteps={consentContext.workflowSteps}
        step={selectedSecondaryPage}
        configVersionId={secondaryConsentConfigVersionId}
        value={consentContext.dualSecondary ? undefined : valueSingle}
        valueSelf={consentContext.dualSecondary ? valueSelf : undefined}
        valueDependent={consentContext.dualSecondary ? valueDependent : undefined}
        onSelect={
          consentContext.dualSecondary
            ? undefined
            : (nextValue: boolean) => secondaryConsent.setToggles((prev) => ({ ...prev, [item.id]: nextValue }))
        }
        onSelectSelf={
          consentContext.dualSecondary
            ? (nextValue: boolean) => secondaryConsent.setToggles((prev) => ({ ...prev, [keySelf]: nextValue }))
            : undefined
        }
        onSelectDependent={
          consentContext.dualSecondary
            ? (nextValue: boolean) => secondaryConsent.setToggles((prev) => ({ ...prev, [keyDep]: nextValue }))
            : undefined
        }
        saving={secondaryConsent.saving}
        onContinue={async () => {
          if (rows) {
            const missingCount = rows.filter(({ key }) => secondaryConsent.toggles[key] === undefined).length
            if (missingCount > 0) {
              track('consent.validation_error', {
                item_id: item.id,
                field: 'secondary_subject_choice',
                missing_count: missingCount,
              })
              return
            }
          } else if (consentContext.dualSecondary) {
            if (valueSelf === undefined || valueDependent === undefined) {
              track('consent.validation_error', {
                item_id: item.id,
                field: 'secondary_dual_choice',
                missing_self: valueSelf === undefined,
                missing_dependent: valueDependent === undefined,
              })
              return
            }
          } else if (valueSingle === undefined) {
            track('consent.validation_error', { item_id: item.id, field: 'secondary_choice' })
            return
          }

          const nextIndex = selectedSecondaryIndex! + 1
          const hasLaterStep = nextIndex < consentConfig.secondaryItems.length
          const to = hasLaterStep
            ? `/consents/secondary/${nextIndex + 2}`
            : featureFlags.enableFastenConnect && acceptsHealthRecordsAccess(secondaryConsent.toggles)
              ? '/health-records/connect'
              : '/home'
          if (!hasLaterStep) {
            const saved = await secondaryConsent.saveAllChoices(
              consentConfig.secondaryItems,
              secondarySubjects,
              consentContext.dualSecondary
            )
            if (!saved) return
          }
          track('consent.stage_continue', { from: path, to, item_id: item.id })
          navigate(to)
        }}
      />
    )
  }

  if (path.startsWith('/consents/secondary/')) {
    return (
      <SecondaryIntroScreen
        hasItems={consentConfig.secondaryItems.length > 0}
        workflowSteps={consentContext.workflowSteps}
      />
    )
  }

  return <ConsentIntroScreen alreadySigned={primaryConsent.confirmed} workflowSteps={consentContext.workflowSteps} />
}

export default ConsentOptions
